import { ref, type Ref } from 'vue'
import { defineStore } from 'pinia'
import { useAuthStore } from './authStore'
import { myRequest } from '@/utils/fetch'

const STORE_NAME = 'app'

export enum EventLocation {
  ZOOM = 'zoom',
  TEAMS = 'teams',
  GOOGLE_MEET = 'google_meet',
  SKYPE = 'skype',
  ONLINE = 'online',
  IN_PERSON = 'in_person'
}

export type Calendar = {
  calendarID: number
  externalID: string
  summary: string
  readOnly: boolean
  lastFullsync: Date
}

export type Meeting = {
  eventID: number
  title: string
  start: string
  end: string
  status: string
  location: EventLocation | null
  attendees: Array<{
    picture: string | null
    initial: string
  }>
}

export type Profile = {
  personID: number
  eventID: number
  createdAt: string
  Person: {
    firstName: string
    lastName: string
    email: string
    profilePicutre: string
    jobTitle: string
    companyName: string
    linkedinUsername: string
  }
}

export type Usage = {
  individual: number
  team: number
  outOf: number
}

interface Subscription {
  current_period_start: Date
  current_period_end: Date
  seats: number
  profileAddOn: number
  teamID: number
  status: 'incomplete' | 'incomplete_expired' | 'trialing' | 'active' | 'past_due' | 'canceled' | 'unpaid' | 'paused'
  setupPayment: boolean
  trialEnd: Date | null
}

export const useAppStore = defineStore(STORE_NAME, () => {
  let localStoreageVal = localStorage.getItem(STORE_NAME)
  const showDialog = ref<boolean>(false)
  const errorType = ref<'Auth' | 'Generic' | null>(null)
  const errorMessage = ref<string>('')
  const useMockData = ref(import.meta.env.DEV) // Flag to control mock data usage - true if running in development mode
  const showAllSections = ref<boolean>(localStoreageVal ? (JSON.parse(localStoreageVal).showAllSections ?? true) : true)

  // Mock data
  const mockMeetings: Meeting[] = [
    {
      eventID: 1,
      title: 'Product Strategy Review',
      start: new Date(Date.now() + 3600000).toISOString(), // 1 hour from now
      end: new Date(Date.now() + 7200000).toISOString(), // 2 hours from now
      status: 'confirmed',
      location: EventLocation.ZOOM,
      attendees: [
        { picture: null, initial: 'JD' },
        { picture: null, initial: 'AS' },
        { picture: null, initial: 'MR' }
      ]
    },
    {
      eventID: 2,
      title: 'Team Standup',
      start: new Date(Date.now() + 86400000).toISOString(), // tomorrow
      end: new Date(Date.now() + 15 * 60 * 1000 + 86400000).toISOString(), // 15 minutes later
      status: 'confirmed',
      location: EventLocation.TEAMS,
      attendees: [
        { picture: null, initial: 'TM' },
        { picture: null, initial: 'JP' },
        { picture: null, initial: 'KL' }
      ]
    },
    {
      eventID: 3,
      title: 'Client Presentation',
      start: new Date(Date.now() + 172800000).toISOString(), // 2 days from now
      end: new Date(Date.now() + 180000000).toISOString(),
      status: 'confirmed',
      location: EventLocation.GOOGLE_MEET,
      attendees: [
        { picture: null, initial: 'AB' },
        { picture: null, initial: 'CD' },
        { picture: null, initial: 'EF' }
      ]
    },
    {
      eventID: 4,
      title: 'Project Kickoff',
      start: new Date(Date.now() - 86400000).toISOString(), // yesterday
      end: new Date(Date.now() - 82800000).toISOString(), // yesterday + 1 hour
      status: 'cancelled',
      location: EventLocation.IN_PERSON,
      attendees: [
        { picture: null, initial: 'GH' },
        { picture: null, initial: 'IJ' },
        { picture: null, initial: 'KL' }
      ]
    }
  ]

  const mockProfiles: Profile[] = [
    {
      personID: 1,
      eventID: 1,
      createdAt: new Date().toISOString(),
      Person: {
        firstName: 'John',
        lastName: 'Doe',
        email: 'john.doe@company.com',
        profilePicutre: 'https://i.pravatar.cc/150?img=1',
        jobTitle: 'Product Manager',
        companyName: 'Tech Corp',
        linkedinUsername: 'johndoe'
      }
    },
    {
      personID: 2,
      eventID: 2,
      createdAt: new Date().toISOString(),
      Person: {
        firstName: 'Sarah',
        lastName: 'Smith',
        email: 'sarah.smith@company.com',
        profilePicutre: 'https://i.pravatar.cc/150?img=2',
        jobTitle: 'UX Designer',
        companyName: 'Design Studio',
        linkedinUsername: 'sarahsmith'
      }
    }
  ]

  const mockCalendars: Calendar[] = [
    {
      calendarID: 1,
      externalID: 'primary',
      summary: 'Work Calendar',
      readOnly: false,
      lastFullsync: new Date()
    },
    {
      calendarID: 2,
      externalID: 'personal',
      summary: 'Personal Calendar',
      readOnly: false,
      lastFullsync: new Date()
    }
  ]

  if (localStoreageVal && JSON.parse(localStoreageVal)?.usage === undefined) {
    localStorage.removeItem(STORE_NAME)
    localStoreageVal = null
    useAuthStore()
      .logout()
      .then(() => {
        window.location.reload()
      })
  }
  const calendars: Ref<Calendar[]> = ref(localStoreageVal ? JSON.parse(localStoreageVal).calendars || [] : [])
  const meetings: Ref<Meeting[]> = ref(localStoreageVal ? JSON.parse(localStoreageVal).meetings || [] : [])
  const profiles: Ref<Profile[]> = ref(localStoreageVal ? JSON.parse(localStoreageVal).profiles || [] : [])
  const usage: Ref<Usage> = ref(
    localStoreageVal ? JSON.parse(localStoreageVal).usage : { individual: 0, team: 0, outOf: 0 }
  )
  const subscription: Ref<Subscription | null> = ref(
    localStoreageVal ? JSON.parse(localStoreageVal).subscription : null
  )
  const lastViewedUpdates = ref<Date>(new Date(localStoreageVal ? JSON.parse(localStoreageVal).lastViewedUpdates : 0))

  const setResult = (result: {
    calendars: Calendar[]
    meetings: Meeting[]
    profiles: Profile[]
    usage: Usage
    subscription: Subscription
    lastViewedUpdates: string
  }) => {
    localStorage.setItem(
      STORE_NAME,
      JSON.stringify({
        ...result,
        showAllSections: showAllSections.value
      })
    )
    calendars.value = result.calendars
    meetings.value = result.meetings
    profiles.value = result.profiles
    usage.value = result.usage
    subscription.value = result.subscription
    lastViewedUpdates.value = new Date(result.lastViewedUpdates)
  }

  const updateLastViewedUpdates = (date: Date) => {
    const storeData = {
      calendars: calendars.value,
      meetings: meetings.value,
      profiles: profiles.value,
      usage: usage.value,
      subscription: subscription.value,
      lastViewedUpdates: date.toISOString()
    }
    localStorage.setItem(STORE_NAME, JSON.stringify(storeData))
    myRequest('POST', 'app/updateLastViewedUpdates', { date: date.toISOString() })
    lastViewedUpdates.value = date
  }

  const updateShowAllSections = (value: boolean) => {
    showAllSections.value = value
    const storeData = {
      calendars: calendars.value,
      meetings: meetings.value,
      profiles: profiles.value,
      usage: usage.value,
      subscription: subscription.value,
      lastViewedUpdates: lastViewedUpdates.value.toISOString(),
      showAllSections: value
    }
    localStorage.setItem(STORE_NAME, JSON.stringify(storeData))
  }

  const fetchData = async () => {
    const result = await myRequest('GET', 'app/home')

    if (useMockData.value) {
      // Combine mock data with actual data
      result.meetings = [...(result.meetings || []), ...mockMeetings]
      result.calendars = [...(result.calendars || []), ...mockCalendars]
      result.profiles = [...(result.profiles || []), ...mockProfiles]
    }

    setResult(result)
    return result
  }

  return {
    calendars,
    meetings,
    profiles,
    usage,
    subscription,
    lastViewedUpdates,
    showDialog,
    errorType,
    errorMessage,
    useMockData,
    showAllSections,
    updateLastViewedUpdates,
    fetchData,
    updateShowAllSections
  }
})
